import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { MatLegacyMenuModule, MatLegacyMenuTrigger } from '@angular/material/legacy-menu';
import { IconComponent } from '@mode/capra';
import { ReportTypes } from '@mode/shared/contract-common';
import { SharedUtilSegmentModule } from '@mode/shared/util-segment';

@Component({
  standalone: true,
  selector: 'mode-report-creation-menu-item',
  imports: [CommonModule, IconComponent, MatLegacyMenuModule, SharedUtilSegmentModule],
  templateUrl: './report-creation-menu-item.component.html',
  styleUrls: ['./report-creation-menu-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportCreationMenuItemComponent implements OnDestroy {
  @Input() item: ReportTypes.ReportCreationMenuItem | undefined;
  @Output() clicked = new EventEmitter<{ event: MouseEvent; testAttribute: string }>();
  @ViewChild(MatLegacyMenuTrigger) menuTrigger!: MatLegacyMenuTrigger;

  private isSubmenuOpen = false;
  private static openSubmenus = new Map<ReportCreationMenuItemComponent, MatLegacyMenuTrigger>();
  private hoverTimeout: any;

  onClick(event: MouseEvent, testAttribute: string): void {
    this.clicked.emit({ event, testAttribute });
  }

  onSubmenuClosed(): void {
    this.isSubmenuOpen = false;
    ReportCreationMenuItemComponent.openSubmenus.delete(this);
  }

  @HostListener('mouseenter')
  onTriggerMouseEnter(): void {
    if (this.item?.subNavigationItems?.length) {
      this.openSubmenuOnHover();
    }
  }

  @HostListener('mouseleave')
  onTriggerMouseLeave(): void {
    this.scheduleSubmenuClose();
  }

  onSubmenuMouseEnter(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  onSubmenuMouseLeave(): void {
    this.scheduleSubmenuClose();
  }

  private openSubmenuOnHover(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    this.hoverTimeout = setTimeout(() => {
      if (this.menuTrigger && !this.isSubmenuOpen) {
        this.openSubmenu(this.menuTrigger);
      }
    }, 150);
  }

  private scheduleSubmenuClose(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }

    this.hoverTimeout = setTimeout(() => {
      if (this.isSubmenuOpen) {
        this.closeSubmenu(this.menuTrigger);
      }
    }, 100);
  }

  private openSubmenu(trigger: MatLegacyMenuTrigger): void {
    this.closeOtherSubmenus();
    trigger.openMenu();
    this.isSubmenuOpen = true;
    ReportCreationMenuItemComponent.openSubmenus.set(this, trigger);
  }

  private closeSubmenu(trigger: MatLegacyMenuTrigger): void {
    trigger.closeMenu();
    this.isSubmenuOpen = false;
    ReportCreationMenuItemComponent.openSubmenus.delete(this);
  }

  private closeOtherSubmenus(): void {
    ReportCreationMenuItemComponent.openSubmenus.forEach((trigger, component) => {
      if (component !== this) {
        trigger.closeMenu();
        component.isSubmenuOpen = false;
      }
    });
    ReportCreationMenuItemComponent.openSubmenus.clear();
  }

  ngOnDestroy(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    if (this.isSubmenuOpen) {
      ReportCreationMenuItemComponent.openSubmenus.delete(this);
    }
  }
}
